<template>
  <article class="enterprise-legal">
    <div class="enterprise-legal__container">
      <NuxtLink :to="localePath('/')" class="enterprise-legal__back">← 返回企业版</NuxtLink>

      <header class="enterprise-legal__header">
        <span>Doclingo 企业版</span>
        <h1>{{ legalMeta.title }}</h1>
        <p>{{ legalMeta.dateLabel }}：{{ legalMeta.date }}</p>
      </header>

      <div class="enterprise-legal__intro">
        {{ legalMeta.intro }}
      </div>

      <section v-for="section in sections" :key="section.title" class="enterprise-legal__section">
        <h2>{{ section.title }}</h2>
        <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
      </section>
    </div>
  </article>
</template>

<script setup lang="ts">
import { useSiteConfig } from '~/config/useSiteConfig'

definePageMeta({ layout: 'enterprise' })

interface LegalSection {
  title: string
  paragraphs: string[]
}

const route = useRoute()
const localePath = useLocalePath()
const { isVipSite } = useSiteConfig()
const { contactEmail, doclingoUrl } = useAppDomainConfig()
const isPrivacy = computed(() => route.params.type === 'userPrivacy')

const termsSections: LegalSection[] = [
  {
    title: '一、服务概述',
    paragraphs: [
      'Doclingo 提供在线文档、图片与文本翻译，以及面向企业的成员协作、术语管理和文件管理等服务。访问或使用本网站及相关服务，即表示您同意遵守本服务协议；如您不同意其中任何内容，请停止使用本服务。',
    ],
  },
  {
    title: '二、使用条件与行为规范',
    paragraphs: [
      '您应具备与自身行为相适应的民事行为能力，并有权代表自己或所属组织接受本协议。未满 18 周岁的用户应在监护人指导和同意下使用本服务。',
      '您应仅将 Doclingo 用于合法目的，并尊重他人的知识产权、隐私权和其他合法权益。请勿上传国家秘密、违法内容、无权处理的受版权保护内容，或与翻译目的无关的敏感个人信息。',
      '您不得绕过安全措施、干扰服务运行、使用脚本或爬虫大规模访问、对系统进行反向工程，或利用本服务开发具有实质竞争关系的产品。',
    ],
  },
  {
    title: '三、账户与企业管理员责任',
    paragraphs: [
      '您应妥善保管账户凭证，并对账户下发生的活动负责。发现未经授权的使用或安全风险时，请及时联系我们。',
      '企业管理员可依据企业授权配置成员、文件和术语库权限。企业用户应确保管理员操作、成员邀请及组织内数据处理具有合法授权。',
    ],
  },
  {
    title: '四、知识产权',
    paragraphs: [
      'Doclingo 的软件、界面、标识、文本、图形、代码及相关内容受知识产权法律保护。我们授予您一项有限的、非独占的、不可转让的许可，仅用于访问和使用本服务。',
      '除本协议明确允许外，您不得复制、修改、分发、出售、出租或以其他方式利用 Doclingo 的服务内容。',
    ],
  },
  {
    title: '五、用户内容',
    paragraphs: [
      '您保留对上传文档、图片、文本、术语及其他内容的合法权利，并保证拥有提交及授权处理这些内容所需的权利。',
      '为完成翻译、文件管理和企业协作，我们仅在提供服务所必需的范围内处理、存储、复制和传输用户内容。该有限授权不会使 Doclingo 取得用户内容的所有权。',
    ],
  },
  {
    title: '六、付费服务',
    paragraphs: [
      '企业套餐的功能、额度、有效期、费用及结算方式以购买页面、订单或双方签署的企业合同为准。除法律另有规定或双方另有书面约定外，已经交付或消耗的服务不支持退款。',
      '如企业合同与本协议就特定服务存在不一致，以企业合同的明确约定为准。',
    ],
  },
  {
    title: '七、免责声明',
    paragraphs: [
      '本服务按照现有技术和可用状态提供。机器翻译结果可能受到原文质量、语言、排版和专业领域影响，我们不保证翻译结果完全准确、完整或适用于特定专业用途。重要内容应由具备资质的人员复核。',
      '我们会持续维护服务的安全与稳定，但不保证服务在任何时间均不中断、无延迟或无错误。',
    ],
  },
  {
    title: '八、责任限制',
    paragraphs: [
      '在适用法律允许的最大范围内，Doclingo 不对因使用或无法使用本服务而产生的间接、偶然、特殊、惩罚性或后果性损失承担责任。',
      '除适用法律禁止限制的责任外，我们就本服务承担的累计责任不超过引发索赔事项发生前 12 个月内您为相关服务实际支付的费用。',
    ],
  },
  {
    title: '九、服务变更、中断与终止',
    paragraphs: [
      '我们可能因维护、升级、安全、不可抗力或业务调整暂停或变更部分服务，并在合理可行的范围内提前通知。',
      '如您违反本协议、从事违法活动或对平台及其他用户造成风险，我们可以限制访问、删除违法内容、暂停或终止账户。',
    ],
  },
  {
    title: '十、法律适用与争议解决',
    paragraphs: [
      '本协议适用服务运营主体所在地的法律。因本协议或本服务产生的争议，双方应先友好协商；协商不成的，可向有管辖权的法院或双方约定的争议解决机构提起处理。',
    ],
  },
  {
    title: '十一、协议更新与联系',
    paragraphs: [
      `我们可能根据法律、技术或服务变化更新本协议，并在本页面公布。重大变更将通过合理方式提示；更新后继续使用服务，视为接受更新内容。如有疑问，请联系 ${contactEmail.value}。`,
    ],
  },
]

const privacySections = computed<LegalSection[]>(() => [
  {
    title: '一、适用范围',
    paragraphs: [
      '本隐私政策适用于 Doclingo 网站及企业版相关服务。企业客户通过合同对数据处理另有明确约定的，相关约定与本政策共同适用。第三方网站或服务有其独立的隐私规则，请在使用前另行查阅。',
    ],
  },
  {
    title: '二、我们收集和使用的信息',
    paragraphs: [
      '账户与企业信息：为完成注册、登录、身份验证、成员管理和客户支持，我们可能处理邮箱、手机号、昵称、头像、企业名称、成员角色及您主动提交的联系信息。',
      '翻译与协作内容：您提交的文档、图片、文本、术语和相关操作记录，仅用于完成翻译、文件管理、协作和您明确使用的相关功能。请勿提交与服务目的无关的敏感个人信息。',
      '设备与日志信息：为保障服务安全和稳定，我们可能处理浏览器类型、设备类型、IP 地址、访问时间、功能使用记录、故障日志和安全审计信息。',
      '订单与服务信息：购买企业套餐时，我们会处理订单、套餐、开票和支付状态；银行卡等原始支付凭证由合规的支付服务提供方处理。',
    ],
  },
  {
    title: '三、处理目的与合法基础',
    paragraphs: [
      '我们基于履行合同、取得同意、遵守法定义务和保障网络安全等合法基础处理信息，用于提供和改进服务、验证身份、处理订单、响应咨询、预防欺诈和满足合规要求。',
      '如需将信息用于与原目的无直接关联的新用途，我们会按照适用法律另行说明并在需要时征得您的同意。',
    ],
  },
  {
    title: '四、信息共享、转让与公开披露',
    paragraphs: [
      '我们不会出售您的个人信息。仅在提供支付、登录、云存储、邮件、统计或在线客服等必要功能时，向经过评估的服务提供方共享实现该功能所需的最少信息，并通过合同约束其处理行为。',
      '发生合并、收购、资产转让或重组时，如涉及个人信息转移，我们会要求新的持有方继续遵守本政策，或依法重新取得授权。',
      '除获得您的明确同意外，我们仅会在法律法规、司法或行政机关依法要求，以及保护用户或公众重大合法权益所必要时公开披露信息。',
    ],
  },
  {
    title: '五、存储地点与期限',
    paragraphs: [
      isVipSite.value
        ? '中国站服务收集和产生的信息原则上存储在中国境内；如确需跨境传输，我们会依法履行必要程序并采取保护措施。'
        : '全球站服务会根据用户所在地区、服务可用性和合同约定使用全球节点处理信息；涉及跨境传输时，我们会采取适用法律要求的保护措施。',
      '我们仅在实现本政策所述目的、履行企业合同及满足法定义务所需的期限内保留信息。翻译文件的保存期限以产品页面、企业合同或管理员配置为准；期限届满后将删除或匿名化处理，法律另有要求的除外。',
    ],
  },
  {
    title: '六、信息安全',
    paragraphs: [
      '我们采用加密传输、访问控制、权限隔离、日志审计、备份与安全监测等合理措施保护信息，并遵循最小必要原则限制员工和服务提供方的访问。',
      '互联网环境无法保证绝对安全。如发生可能影响您权益的安全事件，我们会依照适用法律采取补救措施，并通过合理方式告知事件情况和建议。',
    ],
  },
  {
    title: '七、您的权利与账户注销',
    paragraphs: [
      '在适用法律规定的范围内，您可以查询、更正、复制或删除个人信息，撤回同意、限制或反对特定处理，并通过账户设置或联系我们注销账户。',
      '账户注销后，相关数据将按照法律和合同要求删除或匿名化，且账户内尚未使用的权益可能无法恢复。为保护账户安全，我们可能在响应请求前验证您的身份。',
    ],
  },
  {
    title: '八、企业客户与成员数据',
    paragraphs: [
      '企业客户通常决定其成员账户、权限和组织内业务数据的处理目的与方式。成员如需行使与企业控制数据相关的权利，可先联系所属企业管理员；我们会按照企业客户的合法指示提供协助。',
    ],
  },
  {
    title: '九、未成年人保护',
    paragraphs: [
      'Doclingo 企业版主要面向组织和具备相应民事行为能力的用户。未成年人应在监护人同意和指导下使用服务；如发现未经适当同意处理了未成年人信息，请联系我们处理。',
    ],
  },
  {
    title: '十、政策更新',
    paragraphs: [
      '我们可能根据法律法规、技术和业务变化更新本政策。重大变更将通过页面提示、公告、邮件或其他合理方式告知，并在适用法律要求时重新征得您的同意。',
    ],
  },
  {
    title: '十一、联系我们',
    paragraphs: [
      `运营主体：武汉路客网络科技有限公司。隐私与个人信息保护相关问题可发送至 ${contactEmail.value}，或通过 ${doclingoUrl.value} 提供的客服渠道联系我们。我们会在验证请求后于法律规定期限内答复。`,
    ],
  },
])

const legalMeta = computed(() => isPrivacy.value
  ? {
      title: '隐私政策',
      dateLabel: '更新日期',
      date: '2025年9月6日',
      intro: '武汉路客网络科技有限公司（以下简称“我们”）重视您的隐私和个人信息安全。本政策说明 Doclingo 企业版如何收集、使用、存储、共享和保护信息，以及您可以如何行使相关权利。',
    }
  : {
      title: '服务协议',
      dateLabel: '生效日期',
      date: '2026年1月12日',
      intro: '请在使用 Doclingo 企业版前仔细阅读本协议。您访问、注册、购买或使用本服务，即表示您已经阅读、理解并同意本协议。',
    })

const sections = computed(() => isPrivacy.value ? privacySections.value : termsSections)

useHead({
  title: computed(() => `${legalMeta.value.title} - Doclingo 企业版`),
})
</script>

<style scoped lang="scss">
.enterprise-legal {
  min-height: 100%;
  padding: 56px 24px 88px;
  color: #27313b;
  background: #f6f8fb;

  &__container {
    width: min(920px, 100%);
    margin: 0 auto;
    padding: 52px 64px 68px;
    background: #fff;
    border: 1px solid #e3e8ef;
    border-radius: 12px;
    box-shadow: 0 14px 40px rgba(35, 55, 77, 0.07);
  }

  &__back {
    display: inline-flex;
    margin-bottom: 34px;
    color: #257ee8;
    font-size: 14px;

    &:hover {
      text-decoration: underline;
    }
  }

  &__header {
    padding-bottom: 34px;
    border-bottom: 1px solid #e9edf2;

    span {
      color: #257ee8;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.08em;
    }

    h1 {
      margin: 12px 0 10px;
      color: #18212b;
      font-size: 38px;
      line-height: 1.25;
    }

    p {
      margin: 0;
      color: #7a8490;
      font-size: 14px;
    }
  }

  &__intro {
    margin: 32px 0 40px;
    padding: 18px 20px;
    color: #4d5966;
    font-size: 15px;
    line-height: 1.9;
    background: #f4f8fe;
    border-left: 3px solid #257ee8;
    border-radius: 0 6px 6px 0;
  }

  &__section {
    & + & {
      margin-top: 34px;
    }

    h2 {
      margin: 0 0 14px;
      color: #1f2933;
      font-size: 20px;
      line-height: 1.5;
    }

    p {
      margin: 10px 0 0;
      color: #55616e;
      font-size: 15px;
      line-height: 1.95;
    }
  }
}

@media (max-width: 720px) {
  .enterprise-legal {
    padding: 24px 16px 56px;

    &__container {
      padding: 34px 22px 46px;
    }

    &__header h1 {
      font-size: 30px;
    }
  }
}
</style>
